import type { NavigationButtonProps } from './NavigationButton';
import NavigationButton from './NavigationButton';
import type { TooltipProps } from '../Tooltip';
import Tooltip from '../Tooltip';

export type TooltipButtonProps = Omit<TooltipProps, 'children'> & NavigationButtonProps;

/**
 * A NavigationButton wrapped in a Tooltip component.
 *
 * @param {TooltipButtonProps} props - TooltipNavigationButtonProps
 * @param {ButtonType} [props.type] - Which type (style) of button to use.
 * @param {ButtonSize} [props.size] - Which size should the button be.
 * @param {IconClassName} [props.icon] - If a given icon should be rendered to the left of props.children.
 * @param {boolean} [props.isDisabled] - If the button should be disabled or not.
 * @param {React.ReactChild} [props.children] - The content of the button.
 * @param {(event: React.MouseEvent) => void} props.onClick - Click handler for the button.
 * @param {string | React.ReactNode} props.tooltip - Tooltip text. Can also be a ReactNode.
 * @param {'top' | 'bottom' | 'right' | 'left'} [props.placement] - Placement of the tooltip.
 * @param {boolean} [props.showTooltip] - If the tooltip should be displayed or not (children rendered as fallback).
 */
const TooltipNavigationButton = ({
  tooltip,
  placement,
  showTooltip,
  children,
  ...navButtonProps
}: TooltipButtonProps) => (
  <Tooltip
    tooltip={tooltip}
    placement={placement}
    showTooltip={showTooltip}
  >
    <span className="inline-block">
      <NavigationButton
        // We want to inject a large number of unknown props here, so spreading is necessary.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...navButtonProps}
      >
        {children}
      </NavigationButton>
    </span>
  </Tooltip>
);

export default TooltipNavigationButton;
